// ─────────────────────────────────────────────────────────────────────────────
// graphPicking — GPU id picking.
//
// NO DOM ELEMENT PER NODE, AND NO RAYCAST THROUGH A HUNDRED THOUSAND POINTS. The
// GPU already solved the "what is under this pixel" problem: render the visible
// set once into an offscreen target with each point's INDEX encoded as its
// colour, read the one pixel under the cursor, and map it back.
//
// It costs one draw of a one-pixel scissor region, on demand — never per frame,
// and never a fresh render target per click. One target is allocated and reused.
//
// The picking pass renders the SAME buffers as the visible pass, so what can be
// clicked is exactly what can be seen. A pick set that drifts from the visible
// set is how a user ends up selecting something that is not on screen.
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from 'three';
import { NodeBufferChunk } from './graphBuffers';

const PICK_VERTEX_SHADER = `
attribute float aPickSize;
attribute float aPickPriority;
attribute float aPickAlpha;
attribute float aPickId;

uniform float uPixelRatio;

varying vec3 vPickColor;
varying float vDrop;

void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vec4 clip = projectionMatrix * mv;

  // SEMANTIC PRIORITY AS A DEPTH BIAS. Where two proxies cover the same pixel,
  // depthTest decides — so the structurally larger entity is nudged toward the
  // camera in the PICK pass only. A domain is therefore never stolen by a memory
  // file drawn in front of it, while at deep zoom (where hubs are behind the
  // camera or culled) the file still wins on its own. Visual depth is untouched:
  // this offset exists in the pick scene and nowhere else.
  clip.z -= aPickPriority * 0.0008 * clip.w;
  gl_Position = clip;

  // THE HIT TARGET IS A CONSTANT NUMBER OF SCREEN PIXELS, BY KIND.
  //
  // It deliberately does NOT scale with distance. The previous shader derived
  // the target from the VISUAL size and applied the visual depth scale, so a
  // domain's target shrank as the camera pulled back — at galaxy distance it
  // reached roughly eight pixels while a two-pixel file was floored up to six.
  // That is precisely the reported fault: hubs that would not hover, and deep
  // nodes that felt easier to click than the thing in front of them.
  gl_PointSize = aPickSize * uPixelRatio;

  // A node faded out by the semantic level is still IN this buffer — filters
  // remove nodes, LOD only dims them. An invisible node must not take a click,
  // so it is dropped from the pick pass rather than left as a hidden target.
  vDrop = aPickAlpha < 0.04 ? 1.0 : 0.0;

  // index+1 packed into RGB — 0,0,0 is reserved for "nothing here".
  float id = aPickId + 1.0;
  float r = mod(id, 256.0);
  float g = mod(floor(id / 256.0), 256.0);
  float b = mod(floor(id / 65536.0), 256.0);
  vPickColor = vec3(r, g, b) / 255.0;
}
`;

const PICK_FRAGMENT_SHADER = `
precision mediump float;
varying vec3 vPickColor;
varying float vDrop;

void main() {
  // An LOD-invisible node writes no pick pixel at all.
  if (vDrop > 0.5) discard;
  // Circular, hard-edged, no alpha: the WHOLE disc acquires, and a soft halo
  // would blend two ids into a colour that decodes to a third node that is not
  // there.
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  if (length(uv) > 1.0) discard;
  gl_FragColor = vec4(vPickColor, 1.0);
}
`;

export class GraphPicker {
  private scene = new THREE.Scene();
  private target: THREE.WebGLRenderTarget;
  private material: THREE.ShaderMaterial;
  private pixel = new Uint8Array(4);
  /** Global pick index → node id, across every chunk in draw order. */
  private index: string[] = [];

  /**
   * Phase timings for the LAST pick, and a record of every slow one.
   *
   * Split deliberately: a tail could be shader compile (render), GPU sync
   * (readback) or target setup, and one total number cannot tell them apart.
   */
  readonly timing = { renderMs: 0, readbackMs: 0, totalMs: 0 };
  readonly slowPicks: Array<{
    seq: number; renderMs: number; readbackMs: number; totalMs: number; indexSize: number;
  }> = [];
  private pickSeq = 0;

  constructor(pixelRatio: number, referenceDistance: number) {
    this.material = new THREE.ShaderMaterial({
      vertexShader: PICK_VERTEX_SHADER,
      fragmentShader: PICK_FRAGMENT_SHADER,
      uniforms: {
        uPixelRatio: { value: pixelRatio },
      },
      transparent: false,
      depthTest: true,
      depthWrite: true,
      blending: THREE.NoBlending,
    });

    this.target = new THREE.WebGLRenderTarget(1, 1, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
    });
  }

  /**
   * Rebuild the pick scene from the same chunks the visible pass uses. Called on
   * a buffer rebuild, never per frame.
   */
  setChunks(chunks: NodeBufferChunk[][]): void {
    for (let i = this.scene.children.length - 1; i >= 0; i--) {
      const child = this.scene.children[i] as THREE.Points;
      child.geometry.dispose();
      this.scene.remove(child);
    }
    this.index = [];

    for (let g = 0; g < chunks.length; g++) {
      const group = chunks[g];
      for (let c = 0; c < group.length; c++) {
        const chunk = group[c];
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(chunk.position, 3));
        // The pick pass reads the INTERACTION attributes, never the visual size.
        geometry.setAttribute('aPickSize', new THREE.BufferAttribute(chunk.aPickSize, 1));
        geometry.setAttribute('aPickPriority',
          new THREE.BufferAttribute(chunk.aPickPriority, 1));
        geometry.setAttribute('aPickAlpha', new THREE.BufferAttribute(chunk.aAlpha, 1));

        // Pick ids are re-issued densely here so the encoding never has to carry
        // a hole left by a filtered-out node.
        const ids = new Float32Array(chunk.count);
        for (let i = 0; i < chunk.count; i++) {
          ids[i] = this.index.length;
          this.index.push(chunk.ids[i]);
        }
        geometry.setAttribute('aPickId', new THREE.BufferAttribute(ids, 1));
        geometry.computeBoundingSphere();
        this.scene.add(new THREE.Points(geometry, this.material));
      }
    }
  }

  setPixelRatio(ratio: number): void {
    this.material.uniforms.uPixelRatio.value = ratio;
  }

  /**
   * What is under (x, y) in CSS pixels, or null. The projection is offset so the
   * single rendered pixel IS the pixel under the cursor — cheaper than rendering
   * the frame and reading one pixel out of it.
   */
  pick(
    renderer: THREE.WebGLRenderer,
    camera: THREE.PerspectiveCamera,
    x: number,
    y: number,
    width: number,
    height: number,
  ): string | null {
    if (!this.index.length) return null;

    const ratio = renderer.getPixelRatio();
    const pickCamera = camera.clone();
    pickCamera.setViewOffset(
      Math.floor(width * ratio),
      Math.floor(height * ratio),
      Math.floor(x * ratio),
      Math.floor(y * ratio),
      1,
      1,
    );

    const priorTarget = renderer.getRenderTarget();
    const t0 = performance.now();
    renderer.setRenderTarget(this.target);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);
    renderer.render(this.scene, pickCamera);
    const t1 = performance.now();
    renderer.readRenderTargetPixels(this.target, 0, 0, 1, 1, this.pixel);
    const t2 = performance.now();
    renderer.setRenderTarget(priorTarget);

    this.pickSeq++;
    this.timing.renderMs = t1 - t0;
    this.timing.readbackMs = t2 - t1;
    this.timing.totalMs = t2 - t0;
    // Only the slow path is recorded — a hundred healthy picks are not evidence.
    if (this.timing.totalMs > 20 && this.slowPicks.length < 24) {
      this.slowPicks.push({
        seq: this.pickSeq,
        renderMs: this.timing.renderMs,
        readbackMs: this.timing.readbackMs,
        totalMs: this.timing.totalMs,
        indexSize: this.index.length,
      });
    }

    const id = this.pixel[0] + this.pixel[1] * 256 + this.pixel[2] * 65536;
    if (id === 0) return null;
    return this.index[id - 1] || null;
  }

  dispose(): void {
    for (let i = this.scene.children.length - 1; i >= 0; i--) {
      const child = this.scene.children[i] as THREE.Points;
      child.geometry.dispose();
      this.scene.remove(child);
    }
    this.material.dispose();
    this.target.dispose();
  }
}
